import React, { FC, useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { SizeMe, SizeMeProps } from 'react-sizeme';

import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import SizeMeContent from 'app/design-system/components/SizeMeContent';
import { Tabs } from 'app/design-system/components/Tabs';
import { tFunction, useTranslation } from 'app/lang';
import useBeforeUnloadListener from 'app/lib/hooks/useBeforeUnloadListener';
import BetSlip from 'app/modules/BetSlip';
import { Cleanup, cleanup } from 'app/modules/BetSlip/actions';
import betSlipContext from 'app/modules/BetSlip/contexts/betSlip';
import { SidebarStakes } from 'app/modules/MyStakes';
import { isSSR, SkipSSR } from 'app/packages/components';
import { useComponentWillReceiveProps } from 'app/packages/lib/hooks';
import ErrorBoundary from 'app/widgets/ErrorBoundary';

import { SwitchActiveKey, switchActiveKey } from './actions';
import BetSlipLabel from './BetSlipLabel';
import { Tab } from './constants';
import LiveStream from './LiveStream';
import { selectActiveTab, selectToAllowRefreshWarning } from './selectors';

import dictionary from './Sidebar.dictionary.json';

import styles from './Sidebar.module.scss';

// ---

type SizeMeHeight = SizeMeProps['size']['height'];

function getTabsOptions(t: tFunction) {
  return [
    {
      label: <BetSlipLabel />,
      id: Tab.BET_SLIP,
      dataGaValues: {
        ['data-ga-event']: 'cart-goToCart',
      },
    },
    {
      label: t(dictionary.tabOptionLabel),
      id: Tab.MY_STAKES,
      dataGaValues: {
        ['data-ga-event']: 'cart-goToBids',
      },
    },
  ];
}

type StoreProps = {
  activeTab: Tab;
  activeBeforeUnloadListener: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<SwitchActiveKey> & ThunkDispatchType<Cleanup>;
};

const Sidebar: FC<StoreProps & DispatchProp> = ({
  activeTab,
  activeBeforeUnloadListener,
  dispatch,
}) => {
  useBeforeUnloadListener(activeBeforeUnloadListener);

  const container = useRef<HTMLDivElement>(null);

  const [headerHeight, setHeaderHeight] = useState(0);
  const [containerTopOffset, setContainerTopOffset] = useState(0);

  const { t, lang } = useTranslation();

  const switchActiveKeyHandler = useCallback((key: Tab) => {
    dispatch(switchActiveKey(key));
  }, []);

  const setNewHeaderHeight = (newHeight: SizeMeHeight) => {
    let heightWithTopOffset = newHeight || 0;
    if (container.current) {
      const offset = container.current.getBoundingClientRect().top;
      heightWithTopOffset += offset;

      if (offset !== containerTopOffset) {
        setContainerTopOffset(offset);
      }
    }
    if (heightWithTopOffset !== headerHeight) {
      setHeaderHeight(heightWithTopOffset);
    }
  };

  useComponentWillReceiveProps(() => {
    // TODO: Чистим корзину после смены языка,
    // после того как в корзине будут автоматически обновлятся
    // переводы названий спорта, матча и ставки (возможно чего-то еще что я не заметил)
    // это можно будет убрать
    dispatch(cleanup());
  }, [lang]);

  return (
    <div data-id="sidebar"
         className={styles.container}
         style={{ height: `calc(100vh - ${containerTopOffset}px)` }}
         ref={container}>
      <SkipSSR>
        <SizeMe monitorHeight
                monitorWidth={false}>
          {({ size }: SizeMeProps) => (
            <SizeMeContent trigger={size.height}
                           onTrigger={setNewHeaderHeight}>
              <div>
                <ErrorBoundary>
                  <LiveStream />
                </ErrorBoundary>

                <ErrorBoundary>
                  <Tabs<Tab>
                    type="button"
                    activeTabId={activeTab}
                    tabs={getTabsOptions(t)}
                    className={styles.menuHeader}
                    onChange={switchActiveKeyHandler} />
                </ErrorBoundary>
              </div>
            </SizeMeContent>
          )}
        </SizeMe>

        <div className={styles.tabsContainer}
             data-test="tabs-container">
          {activeTab === Tab.BET_SLIP ? (
            <ErrorBoundary>
              <BetSlip headerHeight={headerHeight} />
            </ErrorBoundary>
          ) : (
            <ErrorBoundary>
              <SidebarStakes headerHeight={headerHeight} />
            </ErrorBoundary>
          )}
        </div>
      </SkipSSR>
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const allowRefreshWarning = selectToAllowRefreshWarning(state);
  const betSlipEntriesSize = isSSR
    ? 0
    : betSlipContext.selectors.entries.selectBets(state).size;

  return {
    activeBeforeUnloadListener: betSlipEntriesSize > 0 && allowRefreshWarning,
    activeTab: selectActiveTab(state),
  };
};

export default connect(mapStateToProps)(Sidebar);
